import { claimGoogleCredentialHandler, initGoogleId } from './gis'

// How long to wait for Google to hand back a fresh credential before treating
// silent re-auth as unavailable. GIS gives no explicit "couldn't do it silently"
// signal we can rely on (the FedCM migration is deprecating the moment-listener
// methods), so a timeout is the robust primary signal. Generous (60s, not a few
// seconds) because the FIRST renewal in a fresh browser profile is interactive:
// FedCM shows a one-time account-confirmation dialog the user must tap, and a
// short timeout would give up underneath it.
const SILENT_REAUTH_TIMEOUT_MS = 60_000

// Obtain a fresh Google ID token with no user interaction, using GIS Automatic
// Sign-In (One Tap / FedCM). Resolves only when Google returns a credential
// silently — i.e. the user still has a live Google session and previously
// approved this app. Rejects on timeout when a visible sign-in would be
// required; callers decide how to degrade (retry, or logout at real expiry).
// NOTE: the caller must verify the returned token belongs to the expected user
// — in a multi-account browser Google may hand back a different account.
export function silentGoogleReauth(): Promise<string> {
  return initGoogleId().then(
    (google) =>
      new Promise<string>((resolve, reject) => {
        let settled = false
        const finish = (fn: () => void) => {
          if (settled) return
          settled = true
          release()
          clearTimeout(timer)
          fn()
        }

        const release = claimGoogleCredentialHandler((credential) =>
          finish(() => resolve(credential)),
        )

        const timer = setTimeout(
          () => finish(() => reject(new Error('Silent Google re-auth timed out'))),
          SILENT_REAUTH_TIMEOUT_MS,
        )

        // Trigger One Tap / FedCM. We intentionally pass no moment listener: the
        // isSkippedMoment/isDismissedMoment status methods are deprecated under
        // FedCM (they log a GSI warning), so the timeout above is our sole
        // "silent re-auth unavailable" signal.
        google.accounts.id.prompt()
      }),
  )
}
